/**
 * THE PAGER OVER THE FEEDBACK PANEL — which alternative answer setup you are writing feedback for.
 *
 * Not the version pager (`VersionSelector`, in the question heading) and not the condition editor
 * (`BranchConditionCarousel`, in the answer setup). This is the third surface branching needs: a
 * branch carries its OWN feedback, because what is correct differs per branch and so must what the
 * block says about it, and the panel has to say which one is on screen.
 *
 * ## Why it exists as a component
 *
 * Six blocks had written it — mcq, dropdown, keyword, fitb, ordering, scale — and a diff across
 * all six found the code identical, differing only in the wording of one comment. A seventh copy
 * was about to be added by Sort, which is the moment a pattern stops being a coincidence.
 *
 * It takes the index and the branches rather than a block, so it has nothing to know about what a
 * branch's payload is: mcq's carries roles, ordering's an order, sort's a partition, and none of
 * that reaches the three strings this draws.
 *
 * ## Graded only
 *
 * Branches are read straight from persisted data, so a block authored with them and later switched
 * to Participation kept showing this above states those branches can never reach. The gate lives
 * here rather than at each call site for the same reason the component does.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { GradingMode } from '@agavi/blocks';

import { IconButton } from '@/components/ui/icon-button';
import { branchConditionLabel } from '@/lib/block-state-labels';

export interface PagerBranch {
  operator: '<' | '<=' | '==' | '>=' | '>' | 'between';
  threshold: string;
  thresholdHigh?: string;
}

export function FeedbackBranchPager({ gradingMode, branches, activeIndex, onActiveIndexChange }: {
  gradingMode: GradingMode;
  branches: PagerBranch[] | undefined;
  activeIndex: number;
  /** An updater, not a value: every caller clamps against the list it already holds. */
  onActiveIndexChange: (next: (i: number) => number) => void;
}): React.ReactNode {
  const { t } = useTranslation();
  if (gradingMode !== 'graded' || !branches || branches.length === 0) return null;

  /* Clamped on READ. A branch deleted from the carousel above can leave the index past the end,
     and the panel must not blank itself while the caller catches up. */
  const index = Math.min(activeIndex, branches.length - 1);
  const active = branches[index];

  return (
    <div className='flex items-center justify-center gap-1.5 border-b border-ws-border bg-ws-surface px-4 py-2'>
      <IconButton
        type='button'
        size='sm'
        aria-label={t('blocks.branching.previousCondition')}
        disabled={index === 0}
        onClick={() => onActiveIndexChange(i => Math.max(0, i - 1))}
      >
        <ChevronLeft className='h-4 w-4' />
      </IconButton>
      {/* ONE rendering of the comparison, INSIDE the announced span. It used to be stated twice
          side by side — once as plain text here, once in an aria-hidden chip beside it, from the
          same derivation ("=0 =0") — and the chip was the aria-hidden copy, so collapsing them is
          also what makes the condition reach a screen reader at all. */}
      <span className='shrink-0 text-xs font-medium tabular-nums text-ws-muted' aria-live='polite'>
        {t('blocks.branching.conditionIndicator', { current: index + 1, total: branches.length })}
        {active ? ` · ${branchConditionLabel(active.operator, active.threshold, active.thresholdHigh)}` : ''}
      </span>
      <IconButton
        type='button'
        size='sm'
        aria-label={t('blocks.branching.nextCondition')}
        disabled={index >= branches.length - 1}
        onClick={() => onActiveIndexChange(i => Math.min(branches.length - 1, i + 1))}
      >
        <ChevronRight className='h-4 w-4' />
      </IconButton>
    </div>
  );
}
